/**
 * BoardLanes — the v2 Conversations board (CRM UI streamline D-8).
 *
 * What changed, and why:
 *
 * 1. TRIAGE LANES REPLACE STATUS CHIPS. The classic board made the recruiter
 *    assemble "what needs me" out of six status chips and a 200-row list. The
 *    lanes answer it directly (see laneGrouping.ts, which is where the opinion
 *    lives and where it is tested); the chips are gone because a lane already
 *    says everything a status chip did, in the order the work matters.
 * 2. THE LIVE GRID IS RETIRED. One approved capability deletion — no view
 *    param is read or written anywhere on the board any more, and the tile
 *    component it rendered is gone from the tree.
 * 3. SLIM, FIXED-HEIGHT ROWS. A lane over 60 rows renders through react-window;
 *    below that it renders the identical DOM plainly, so the virtualizer is an
 *    implementation detail rather than a second row component to keep in sync.
 * 4. ONE FILTER BAR. Search (client-side, across every lane) + study + a single
 *    Filters popover holding the five server toggles, whose setters are the
 *    console's own — flipping one refetches through the existing cycle.
 *
 * This component is PURELY PROPS-DRIVEN: it never calls the API and never
 * re-implements an action. Data and handlers arrive from ConsoleV2, which owns
 * the fetch cycle and the URL, so selection here is a callback (the URL write
 * is the console's) and the workspace on the right is the classic
 * ConversationWorkspace, reused verbatim.
 */
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bot, ChevronDown, MessageSquareText, SlidersHorizontal, X } from 'lucide-react';
import { FixedSizeList } from 'react-window';
import type { ListChildComponentProps } from 'react-window';
import {
  ConversationWorkspace,
  type BoardRow as BoardRowData,
} from '../../AgentConsole';
import type { DraftRow } from '../../../../components/agent/shared';
import {
  FilterBar, FilterChip, FilterSelect, SearchInput, SegmentedTabs, outlineBtnCls,
  type SegTab,
} from '../../../../components/filters';
import { CountBadge, Z } from '../../../../components/ui';
import { studyInternalLabel, type StudyOption } from '../../../../types';
import BoardRow, { useBoardRowHeight } from './BoardRow';
import {
  LANE_ORDER, RECENT_REPLY_MINUTES, groupBoardRows, matchesBoardQuery,
  type Lane, type LaneKey,
} from './laneGrouping';

/**
 * Above this many rows a lane virtualizes. Chosen against the real shape of
 * the data rather than a round number: the board fetches at most 200 open
 * rows, so a lane under ~60 is a page or two of scrolling that the browser
 * handles better than a windowing library would (no absolute positioning, no
 * scroll listeners, no measurement), while a lane that swallows most of the
 * 200 is exactly where DOM count starts to cost.
 */
const VIRTUALIZE_ABOVE = 60;
/** A virtualized lane never eats the whole card — five of them share it. */
const LANE_MAX_HEIGHT = 480;

/** Header tooltips: the lane's rule, in one line, where it is being applied. */
const LANE_HINT: Record<LaneKey, string> = {
  needs_you: 'Held for review, flagged by the guardrails, or a draft is waiting for approval — oldest first.',
  active_now: `The agent is writing, or somebody texted in the last ${RECENT_REPLY_MINUTES} minutes — newest reply first.`,
  waiting_on_lead: 'Live threads where we spoke last — the ball is in the lead’s court.',
  scheduled_idle: 'Paused, booked for later, or simply quiet — nothing is owed right now.',
  recently_closed: 'The most recently closed conversations, newest first.',
};

export interface BoardLanesProps {
  /** Open statuses (active / paused / awaiting_review), server-capped at 200. */
  board: BoardRowData[];
  /** status=closed, server-capped at 25 — the Recently-closed lane. */
  closedBoard: BoardRowData[];
  /** Conversations with a draft waiting (drafts + guardrail-suppressed). */
  draftConvoIds: Set<string>;
  /** From the URL's ?conversation= — the console owns the param. */
  selectedConvoId: string | null;
  onSelectConversation: (id: string | null) => void;
  nowTs: number;
  busyId: string | null;
  refreshTick: number;
  studies: StudyOption[];
  // The shared server-filter state — the SAME setters the classic board uses.
  flagsOnly: boolean;
  setFlagsOnly: (v: boolean) => void;
  recentReplyOnly: boolean;
  setRecentReplyOnly: (v: boolean) => void;
  engagedOnly: boolean;
  setEngagedOnly: (v: boolean) => void;
  modeFilter: '' | 'copilot' | 'auto';
  setModeFilter: (v: '' | 'copilot' | 'auto') => void;
  studyFilter: string;
  setStudyFilter: (v: string) => void;
  // The console's own handlers, passed straight through to the workspace.
  onConversationAction: (id: string, action: 'pause' | 'resume' | 'takeover' | 'close', body?: { reason?: string }) => Promise<void>;
  onEscalationAction: (id: string, action: 'claim' | 'resolve') => Promise<void>;
  onApprove: (t: DraftRow, text?: string) => Promise<void>;
  onReject: (t: DraftRow, reason: string) => Promise<void>;
  /** Part of the console contract; this surface raises no errors of its own. */
  onError: (msg: string) => void;
}

// ---------------------------------------------------------------------------
// The Filters popover — the five server toggles behind one button.
// ---------------------------------------------------------------------------

interface FiltersMenuProps {
  flagsOnly: boolean;
  setFlagsOnly: (v: boolean) => void;
  recentReplyOnly: boolean;
  setRecentReplyOnly: (v: boolean) => void;
  engagedOnly: boolean;
  setEngagedOnly: (v: boolean) => void;
  modeFilter: '' | 'copilot' | 'auto';
  setModeFilter: (v: '' | 'copilot' | 'auto') => void;
  activeCount: number;
}

/**
 * Five chips that used to sit in the toolbar all day now live behind one
 * button with a count — the toolbar is chrome, and chrome that never changes
 * is chrome that gets scrolled past.
 *
 * Popover discipline (the Phase-3 GuideAgent pattern): the panel stays MOUNTED
 * and toggles with `hidden`; Escape is handled on this node in the bubble
 * phase (no window listener, and never stopped — a confirm layer above must
 * still be able to see it) and returns focus to the button, which is where the
 * keyboard user was; an outside mousedown closes without stealing focus back.
 */
function FiltersMenu({
  flagsOnly, setFlagsOnly, recentReplyOnly, setRecentReplyOnly,
  engagedOnly, setEngagedOnly, modeFilter, setModeFilter, activeCount,
}: FiltersMenuProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const close = () => { setOpen(false); btnRef.current?.focus(); };

  return (
    <div
      ref={wrapRef}
      className="relative inline-block flex-shrink-0"
      onKeyDown={(e) => { if (e.key === 'Escape' && open) close(); }}
    >
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Narrow what the server sends: flags, recent replies, engagement, and mode"
        className={outlineBtnCls(activeCount > 0)}
      >
        <SlidersHorizontal className="h-4 w-4" />
        Filters
        {activeCount > 0 && (
          <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-primary-600 px-1.5 text-xs font-medium text-white">
            {activeCount}
          </span>
        )}
      </button>

      <div
        role="dialog"
        aria-label="Conversation filters"
        className={`absolute right-0 top-full ${Z.overlay} mt-1.5 w-64 max-w-[min(16rem,calc(100vw-2.5rem))] rounded-xl border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-600 dark:bg-gray-900 ${open ? '' : 'hidden'}`}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Filters
          </h2>
          <button
            type="button"
            onClick={close}
            aria-label="Close filters"
            className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg p-1.5 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-700 dark:hover:text-gray-200 sm:min-h-0 sm:min-w-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          <FilterChip
            active={flagsOnly}
            onClick={() => setFlagsOnly(!flagsOnly)}
            title="Only conversations the guardrails have flagged"
          >
            Has flags
          </FilterChip>
          <FilterChip
            active={recentReplyOnly}
            onClick={() => setRecentReplyOnly(!recentReplyOnly)}
            title={`Only conversations where the lead came back to us in the last ${RECENT_REPLY_MINUTES} minutes`}
          >
            {`Replied <${RECENT_REPLY_MINUTES}m`}
          </FilterChip>
          <FilterChip
            active={engagedOnly}
            onClick={() => setEngagedOnly(!engagedOnly)}
            title="Only conversations the lead has answered at least once since it opened"
          >
            Engaged
          </FilterChip>
          <FilterChip
            active={modeFilter === 'copilot'}
            onClick={() => setModeFilter(modeFilter === 'copilot' ? '' : 'copilot')}
            title="Every reply waits for a human"
          >
            Copilot
          </FilterChip>
          <FilterChip
            active={modeFilter === 'auto'}
            onClick={() => setModeFilter(modeFilter === 'auto' ? '' : 'auto')}
            title="The agent sends within its guardrails"
          >
            Auto
          </FilterChip>
        </div>
        <p className="mt-2 text-[11px] leading-relaxed text-gray-400 dark:text-gray-500">
          These narrow what the server sends — the board reloads when you change one.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// One lane: a disclosure header over plain or virtualized rows.
// ---------------------------------------------------------------------------

interface LaneItemData {
  rows: BoardRowData[];
  selectedId: string | null;
  draftConvoIds: Set<string>;
  nowTs: number;
  onSelect: (id: string) => void;
}

/** react-window's item renderer — the SAME BoardRow the plain branch renders,
 *  handed the library's absolute-positioning style. The lane's scrollToItem
 *  owns deep-link scrolling here, so the row's own scroll is off (seam-4 F2). */
const VirtualRow = memo(function VirtualRow({ index, style, data }: ListChildComponentProps<LaneItemData>) {
  const row = data.rows[index];
  return (
    <BoardRow
      row={row}
      style={style}
      selected={data.selectedId === row.id}
      hasDraft={data.draftConvoIds.has(row.id)}
      nowTs={data.nowTs}
      onSelect={data.onSelect}
      scrollOnSelect={false}
    />
  );
});

interface LaneSectionProps {
  lane: Lane;
  expanded: boolean;
  onToggle: (key: LaneKey) => void;
  selectedConvoId: string | null;
  draftConvoIds: Set<string>;
  nowTs: number;
  onSelect: (id: string) => void;
  rowHeight: number;
  /** Visibility only — below `sm` the picker shows ONE lane (`max-sm:hidden`
   *  on the other four). The section is never unmounted, so its scroll
   *  position, its fold and its virtualizer survive a lane switch. */
  className?: string;
}

function LaneSection({
  lane, expanded, onToggle, selectedConvoId, draftConvoIds, nowTs, onSelect, rowHeight,
  className = '',
}: LaneSectionProps) {
  const listRef = useRef<FixedSizeList<LaneItemData>>(null);
  const rows = lane.rows;
  const virtualized = rows.length > VIRTUALIZE_ABOVE;
  const selectedIndex = selectedConvoId ? rows.findIndex((r) => r.id === selectedConvoId) : -1;

  // A virtualized lane may not have the selected row MOUNTED at all — the row
  // cannot scroll itself into view if it does not exist. 'smart' leaves an
  // already-visible row where it is instead of yanking the list. Latched per
  // SELECTION, not per index (seam-4 F2): a refetch that reorders the lane
  // shifts the selected row's index, and re-firing on that would yank the list
  // back mid-scan every time an inbound message lands.
  const scrolledForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedConvoId) { scrolledForRef.current = null; return; }
    if (!virtualized || !expanded || selectedIndex < 0) return;
    if (scrolledForRef.current === selectedConvoId) return;
    scrolledForRef.current = selectedConvoId;
    listRef.current?.scrollToItem(selectedIndex, 'smart');
  }, [virtualized, expanded, selectedIndex, selectedConvoId]);

  const itemData = useMemo<LaneItemData>(
    () => ({ rows, selectedId: selectedConvoId, draftConvoIds, nowTs, onSelect }),
    [rows, selectedConvoId, draftConvoIds, nowTs, onSelect],
  );

  const bodyId = `board-lane-${lane.key}`;
  return (
    <div id={`board-lane-wrap-${lane.key}`} className={className}>
      <button
        type="button"
        onClick={() => onToggle(lane.key)}
        aria-expanded={expanded}
        aria-controls={bodyId}
        title={LANE_HINT[lane.key]}
        className={`sticky top-0 ${Z.rail} flex min-h-[44px] w-full items-center gap-2 border-b border-gray-200 bg-gray-50 px-3 py-1.5 text-left transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 dark:border-gray-700 dark:bg-gray-900/70 dark:hover:bg-gray-700/50 sm:min-h-0`}
      >
        <ChevronDown
          aria-hidden="true"
          className={`h-3.5 w-3.5 flex-shrink-0 text-gray-400 transition-transform duration-150 ${expanded ? '' : '-rotate-90'}`}
        />
        <span className="truncate text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {lane.label}
        </span>
        <CountBadge
          count={rows.length}
          tone={lane.key === 'needs_you' ? 'alert' : 'primary'}
          className="ml-auto flex-shrink-0"
        />
      </button>

      {/* Hidden, never unmounted: a lane keeps its scroll position (and its
          virtualizer) across a collapse. */}
      <div id={bodyId} className={expanded ? '' : 'hidden'}>
        {rows.length === 0 ? (
          <p className="border-b border-gray-100 px-3 py-2.5 text-xs text-gray-400 dark:border-gray-700/60 dark:text-gray-500">
            none right now
          </p>
        ) : virtualized ? (
          <FixedSizeList<LaneItemData>
            ref={listRef}
            height={Math.min(rows.length * rowHeight, LANE_MAX_HEIGHT)}
            width="100%"
            itemCount={rows.length}
            itemSize={rowHeight}
            itemData={itemData}
            itemKey={(index, data) => data.rows[index].id}
            overscanCount={6}
          >
            {VirtualRow}
          </FixedSizeList>
        ) : (
          rows.map((r) => (
            <BoardRow
              key={r.id}
              row={r}
              selected={r.id === selectedConvoId}
              hasDraft={draftConvoIds.has(r.id)}
              nowTs={nowTs}
              onSelect={onSelect}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The board.
// ---------------------------------------------------------------------------

export default function BoardLanes({
  board, closedBoard, draftConvoIds, selectedConvoId, onSelectConversation,
  nowTs, busyId, refreshTick, studies,
  flagsOnly, setFlagsOnly, recentReplyOnly, setRecentReplyOnly,
  engagedOnly, setEngagedOnly, modeFilter, setModeFilter, studyFilter, setStudyFilter,
  onConversationAction, onEscalationAction, onApprove, onReject,
}: BoardLanesProps) {
  const [query, setQuery] = useState('');
  // Collapse is per-device session state, not a URL concern: recently-closed
  // starts folded because it is reference material, not work.
  const [collapsed, setCollapsed] = useState<Partial<Record<LaneKey, boolean>>>({ recently_closed: true });
  // BELOW `sm` ONLY: which lane the phone is showing. Five stacked collapsibles
  // inside a 70vh box is a scroll maze on a 375px screen — one lane at a time
  // with a segmented picker is the same information, paged. From `sm` up all
  // five render exactly as before and this state is inert (the picker is
  // hidden, the `max-sm:hidden` it drives does not apply). It is state rather
  // than a URL param for the same reason `collapsed` is: it describes this
  // device's viewport, not the thing being looked at.
  const [activeLane, setActiveLane] = useState<LaneKey>(LANE_ORDER[0]);
  const rowHeight = useBoardRowHeight();

  const toggleLane = useCallback((key: LaneKey) => {
    setCollapsed((c) => ({ ...c, [key]: !c[key] }));
  }, []);

  // A deep link can land on a conversation whose row sits in a COLLAPSED lane
  // (Recently closed starts folded) — the workspace opens either way, but the
  // list should show where the row lives (seam-4 F3). Once per selection, so a
  // recruiter who then folds the lane by hand is respected.
  const expandedForRef = useRef<string | null>(null);

  // Stable across renders so the memoized rows (and the virtualizer's
  // itemData) only change when the DATA does — the console is free to hand us
  // a fresh callback on every tick.
  const selectRef = useRef(onSelectConversation);
  useEffect(() => { selectRef.current = onSelectConversation; });
  const handleSelect = useCallback((id: string) => { selectRef.current(id); }, []);
  const handleBack = useCallback(() => { selectRef.current(null); }, []);

  // The search box is the ONE client-side filter: it re-groups the lanes
  // rather than filtering a flat list, so the headers always count what is
  // actually under them.
  const lanes = useMemo(() => {
    const open = query.trim() ? board.filter((r) => matchesBoardQuery(r, query)) : board;
    const closed = query.trim() ? closedBoard.filter((r) => matchesBoardQuery(r, query)) : closedBoard;
    return groupBoardRows(open, closed, draftConvoIds, nowTs);
  }, [board, closedBoard, draftConvoIds, nowTs, query]);

  useEffect(() => {
    if (!selectedConvoId || expandedForRef.current === selectedConvoId) return;
    expandedForRef.current = selectedConvoId;
    const lane = lanes.find((l) => l.rows.some((r) => r.id === selectedConvoId));
    if (!lane) return;
    if (collapsed[lane.key]) setCollapsed((c) => ({ ...c, [lane.key]: false }));
    // Same latch, same reason, one tier down: below `sm` a folded lane is not
    // the only way to hide the row — a lane the picker is not showing hides it
    // too. Harmless from `sm` up, where every lane is on screen anyway.
    setActiveLane(lane.key);
  }, [selectedConvoId, lanes, collapsed]);

  const totalRows = lanes.reduce((n, l) => n + l.rows.length, 0);
  // The phone picker's tabs. Label and count are the LANE'S OWN — the same
  // grouping the headers render from, so the tab and the header can never
  // disagree. Nothing here counts anything.
  const laneTabs = useMemo<SegTab[]>(
    () => lanes.map((l) => ({ value: l.key, label: l.label, count: l.rows.length })),
    [lanes],
  );
  /** Narrow the picker's string back to a LaneKey through the lanes themselves. */
  const pickLane = useCallback((value: string) => {
    const lane = lanes.find((l) => l.key === value);
    if (lane) setActiveLane(lane.key);
  }, [lanes]);

  // The right-hand summary (shown once nothing is selected) lists the same
  // lanes as the left panel — clicking one used to do nothing at all, which
  // read as broken given the "click a row" copy right underneath it. Jumping
  // there mirrors the phone picker's intent: unfold the lane if it was
  // folded (recently_closed starts folded) and bring it into view.
  const goToLane = useCallback((key: LaneKey) => {
    setCollapsed((c) => (c[key] ? { ...c, [key]: false } : c));
    setActiveLane(key);
    requestAnimationFrame(() => {
      document.getElementById(`board-lane-wrap-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const menuFilterCount =
    (flagsOnly ? 1 : 0) + (recentReplyOnly ? 1 : 0) + (engagedOnly ? 1 : 0) + (modeFilter ? 1 : 0);
  // The empty state must never read as "the agent has nothing" while a filter
  // is narrowing the board — the search box counts as one.
  const filtersActive = menuFilterCount > 0 || !!studyFilter || !!query.trim();

  return (
    <div className="mt-3">
      <FilterBar>
        <SearchInput
          value={query}
          onChange={setQuery}
          placeholder="Filter by name, study, or message…"
          label="Filter conversations by name, study, or message"
          className="flex-1 basis-48 sm:basis-64 sm:max-w-[20rem]"
        />
        <FilterSelect
          value={studyFilter}
          onChange={setStudyFilter}
          label="Filter by study"
          auto
          className="max-w-[220px] flex-shrink-0"
        >
          <option value="">All studies</option>
          {studies.map((s) => (
            <option key={s.id || s.slug} value={s.id || ''}>{studyInternalLabel(s)}</option>
          ))}
        </FilterSelect>
        <FiltersMenu
          flagsOnly={flagsOnly}
          setFlagsOnly={setFlagsOnly}
          recentReplyOnly={recentReplyOnly}
          setRecentReplyOnly={setRecentReplyOnly}
          engagedOnly={engagedOnly}
          setEngagedOnly={setEngagedOnly}
          modeFilter={modeFilter}
          setModeFilter={setModeFilter}
          activeCount={menuFilterCount}
        />
      </FilterBar>

      {/* Master–detail, classic's own shape: the list is never unmounted, only
          hidden, so a phone that opens a thread comes back to the same scroll
          position and the same collapsed lanes. */}
      <div className="mt-3 flex flex-col gap-3 lg:flex-row lg:items-start">
        <div className={`w-full flex-shrink-0 lg:block lg:w-[320px] xl:w-[360px] ${selectedConvoId ? 'hidden' : ''}`}>
          {totalRows === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center dark:border-gray-600">
              <Bot className="mx-auto mb-4 h-12 w-12 text-gray-300 dark:text-gray-600" />
              {filtersActive ? (
                <>
                  <p className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    {recentReplyOnly
                      ? `No lead has replied in the last ${RECENT_REPLY_MINUTES} minutes`
                      : 'No conversations match these filters'}
                  </p>
                  <p className="mx-auto mt-1 max-w-sm text-sm text-gray-500 dark:text-gray-400">
                    Nothing matches the filters above — clear them to see the rest of the board.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-lg font-medium text-gray-900 dark:text-gray-100">No agent conversations</p>
                  <p className="mx-auto mt-1 max-w-sm text-sm text-gray-500 dark:text-gray-400">
                    Conversations appear here when a study is agent-enabled, or when you hand a lead to the agent from their page.
                  </p>
                </>
              )}
            </div>
          ) : (
            <>
              {/* Phone: one lane at a time. Five stacked collapsibles inside a
                  70vh box is a scroll maze at 375px, so the picker pages them —
                  and the lane's rule, which was a `title` a touch device can
                  never see, becomes a line of text under it. Both are
                  `sm:hidden`: from `sm` up the card below is exactly what it
                  has always been, and the picker's state is inert. */}
              <div className="mb-2 sm:hidden">
                <SegmentedTabs
                  tabs={laneTabs}
                  value={activeLane}
                  onChange={pickLane}
                  label="Board lanes"
                />
                <p className="mt-1 truncate text-[11px] text-gray-500 dark:text-gray-400">
                  {LANE_HINT[activeLane]}
                </p>
              </div>

              <div className="max-h-[70vh] overflow-y-auto overflow-x-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
                {lanes.map((lane) => (
                  <LaneSection
                    key={lane.key}
                    lane={lane}
                    expanded={!collapsed[lane.key]}
                    onToggle={toggleLane}
                    selectedConvoId={selectedConvoId}
                    draftConvoIds={draftConvoIds}
                    nowTs={nowTs}
                    onSelect={handleSelect}
                    rowHeight={rowHeight}
                    className={lane.key === activeLane ? '' : 'max-sm:hidden'}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <div className={`min-w-0 flex-1 ${selectedConvoId ? '' : 'hidden lg:block'}`}>
          {selectedConvoId ? (
            <ConversationWorkspace
              conversationId={selectedConvoId}
              refreshTick={refreshTick}
              nowTs={nowTs}
              busyId={busyId}
              onBack={handleBack}
              onConversationAction={onConversationAction}
              onEscalationAction={onEscalationAction}
              onApprove={onApprove}
              onReject={onReject}
            />
          ) : (
            /* Nothing selected, laptop only (below `lg` the list IS the view,
               and this column is hidden). The right half stops being blank
               space and becomes the board's shape at a glance. */
            <div className="flex h-full min-h-[240px] items-center justify-center rounded-xl border border-dashed border-gray-300 p-8 text-center dark:border-gray-600">
              <div className="w-full max-w-xs">
                <MessageSquareText className="mx-auto mb-3 h-10 w-10 text-gray-300 dark:text-gray-600" />
                <dl className="divide-y divide-gray-200 text-left dark:divide-gray-700">
                  {lanes.map((l) => (
                    <div
                      key={l.key}
                      role="button"
                      tabIndex={0}
                      onClick={() => goToLane(l.key)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goToLane(l.key); }
                      }}
                      className="flex cursor-pointer items-baseline justify-between gap-3 rounded py-1.5 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 dark:hover:bg-gray-700/40"
                    >
                      <dt className="min-w-0 truncate text-sm text-gray-600 dark:text-gray-300" title={LANE_HINT[l.key]}>
                        {l.label}
                      </dt>
                      <dd className="flex-shrink-0 text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                        {l.rows.length}
                      </dd>
                    </div>
                  ))}
                </dl>
                <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                  Click a lane to open it on the left, then click a row to read the full thread — both directions, drafts, and escalations in one place.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
